<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import apiClient from "@/apiClient.js";
import ListSearch from "@/components/ListSearch.vue";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import {
  ArrowPathIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  CheckBadgeIcon,
  ChevronDownIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  IdentificationIcon,
  MapIcon,
  PlusCircleIcon,
  SparklesIcon,
  TrophyIcon,
  UserGroupIcon,
} from "@heroicons/vue/24/solid";

dayjs.extend(relativeTime);

const router = useRouter();
const route = useRoute();

const dashboard = ref({
  summary: {},
  top_workers: [],
  insights: {
    skill_mix: [],
    readiness: [],
    recent_joiners: [],
    missing_core_records: [],
  },
  training_alerts: {
    summary: {},
    staff: [],
    course_breakdown: [],
    alert_lookup: {},
  },
});

const dashboardLoading = ref(false);
const dashboardError = ref("");
const roster = ref([]);
const rosterLoading = ref(false);
const rosterError = ref("");
const rosterPage = ref(1);
const rosterLastPage = ref(1);
const rosterTotal = ref(0);
const rosterFilter = ref(route.query?.af ?? "active");
const selectedMonth = ref(dayjs().format("YYYY-MM"));
const photos = ref({});
const requestedPhotoIds = new Set();

const monthLabel = computed(() =>
  dayjs(`${selectedMonth.value}-01`).format("MMMM YYYY"),
);
const alertLookup = computed(() => dashboard.value.training_alerts?.alert_lookup ?? {});
const summary = computed(() => dashboard.value.summary ?? {});
const skillMix = computed(() => dashboard.value.insights?.skill_mix ?? []);
const readiness = computed(() => dashboard.value.insights?.readiness ?? []);
const recentJoiners = computed(() => dashboard.value.insights?.recent_joiners ?? []);
const missingCoreRecords = computed(
  () => dashboard.value.insights?.missing_core_records ?? [],
);
const courseBreakdown = computed(
  () => dashboard.value.training_alerts?.course_breakdown ?? [],
);
const trainingWatchlist = computed(
  () => dashboard.value.training_alerts?.staff ?? [],
);
const filteredRoster = computed(() => roster.value);

const recommendations = computed(() => {
  const items = [];
  const topSkill = skillMix.value[0];

  if ((summary.value?.mandatory_gaps ?? 0) > 0) {
    items.push({
      title: "Close mandatory course gaps",
      body: `${summary.value.mandatory_gaps} mandatory training gaps are still open across the active roster.`,
      tone: "danger",
    });
  }

  if ((summary.value?.training_expired ?? 0) > 0) {
    items.push({
      title: "Prioritise expired training",
      body: `${summary.value.training_expired} course records are already expired and should be booked first.`,
      tone: "warning",
    });
  }

  if (missingCoreRecords.value.length > 0) {
    const highestGap = missingCoreRecords.value[0];
    items.push({
      title: "Tidy onboarding records",
      body: `${highestGap.staff.name} currently has ${highestGap.missing_count} missing core record${highestGap.missing_count === 1 ? "" : "s"}.`,
      tone: "neutral",
    });
  }

  if (topSkill?.percentage >= 40) {
    items.push({
      title: "Reduce skill concentration",
      body: `${topSkill.name} makes up ${topSkill.percentage}% of active primary skills. Cross-training would reduce roster fragility.`,
      tone: "accent",
    });
  }

  if (recentJoiners.value.length > 0) {
    items.push({
      title: "Support new joiners",
      body: `${recentJoiners.value.length} recent joiner${recentJoiners.value.length === 1 ? "" : "s"} are visible in the current roster snapshot.`,
      tone: "accent",
    });
  }

  return items.slice(0, 4);
});

const formatDate = (value, format = "DD MMM YYYY") =>
  value ? dayjs(value).format(format) : "Not set";
const formatRelative = (value) => (value ? dayjs(value).fromNow() : "Not set");

const getInitials = (name) => {
  if (!name) {
    return "?";
  }

  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
};

const loadPhoto = async (staff) => {
  if (!staff?.id || !staff?.photo) {
    return;
  }

  if (photos.value[staff.id] || requestedPhotoIds.has(staff.id)) {
    return;
  }

  requestedPhotoIds.add(staff.id);

  try {
    const { data } = await apiClient.get(`/api/staff/${staff.id}/photo`);
    if (data?.image) {
      photos.value = {
        ...photos.value,
        [staff.id]: data.image,
      };
    }
  } catch (error) {
    console.error("Failed to load staff photo", error);
  }
};

const listSearchOutput = (staff) =>
  `${staff.contact?.name || "Unknown"} [${staff.contact?.email || "no email"}]`;

const goToStaff = (staff) => {
  if (!staff?.id) {
    return;
  }

  router.push({
    name: "staff-view",
    params: { id: staff.id },
  });
};

const changeRosterFilter = (filter) => {
  rosterFilter.value = filter;
  rosterPage.value = 1;
  fetchRoster();
};

const changePage = (delta) => {
  const nextPage = rosterPage.value + delta;
  if (nextPage < 1 || nextPage > rosterLastPage.value) {
    return;
  }

  rosterPage.value = nextPage;
  fetchRoster();
};

const fetchDashboard = async () => {
  dashboardLoading.value = true;
  dashboardError.value = "";

  try {
    const { data } = await apiClient.get("/api/staff/dashboard", {
      params: {
        month: selectedMonth.value,
      },
    });
    dashboard.value = data ?? dashboard.value;
  } catch (error) {
    console.error("Unable to load staff dashboard", error);
    dashboardError.value = "Unable to load staff dashboard.";
  } finally {
    dashboardLoading.value = false;
  }
};

const fetchRoster = async () => {
  rosterLoading.value = true;
  rosterError.value = "";

  try {
    const params = {
      page: rosterPage.value,
    };

    if (rosterFilter.value === "active") {
      params.active = "true";
    } else if (rosterFilter.value === "inactive") {
      params.active = "false";
    }

    const { data } = await apiClient.get("/api/staff", { params });
    roster.value = Array.isArray(data?.data) ? data.data : [];
    rosterPage.value = data?.current_page ?? 1;
    rosterLastPage.value = data?.last_page ?? 1;
    rosterTotal.value = data?.total ?? roster.value.length;
    roster.value.forEach(loadPhoto);
  } catch (error) {
    console.error("Unable to load staff roster", error);
    rosterError.value = "Unable to load staff roster.";
  } finally {
    rosterLoading.value = false;
  }
};

const refreshAll = async () => {
  await Promise.all([fetchDashboard(), fetchRoster()]);
};

const trainingTone = (stats) => {
  if ((stats?.expired_count ?? 0) > 0) {
    return "badge-danger";
  }
  if ((stats?.missing_count ?? 0) > 0) {
    return "badge-warning";
  }
  if ((stats?.expiring_soon_count ?? 0) > 0) {
    return "badge-soon";
  }
  return "badge-clear";
};

const recommendationTone = (tone) => {
  switch (tone) {
    case "danger":
      return "recommendation-danger";
    case "warning":
      return "recommendation-warning";
    case "accent":
      return "recommendation-accent";
    default:
      return "recommendation-neutral";
  }
};

watch(selectedMonth, () => {
  fetchDashboard();
});

onMounted(() => {
  refreshAll();
});
</script>

<template>
  <div class="staff-dashboard space-y-6">
    <section class="box staff-shell p-0 overflow-hidden">
      <header class="staff-shell-header">
        <div class="space-y-2">
          <h1 class="staff-shell-title">
            Staff
          </h1>
        </div>

        <div class="staff-shell-actions">
          <router-link to="/staff/map" class="btn flex items-center gap-2">
            <MapIcon class="h-4 w-4" />
            Staff map
          </router-link>
          <router-link to="/staff/create" class="btn accent flex items-center gap-2">
            <PlusCircleIcon class="h-4 w-4" />
            Add staff
          </router-link>
        </div>
      </header>

      <div class="staff-shell-toolbar">
        <div class="staff-shell-searches">
          <ListSearch
            class="flex-1"
            url="/api/staff/search?query="
            :output="listSearchOutput"
            @selected="goToStaff"
          />
        </div>

        <div class="staff-shell-filters">
          <button class="filter-pill" :class="{ 'filter-pill-active': rosterFilter === 'all' }" @click="changeRosterFilter('all')">
            All
          </button>
          <button class="filter-pill" :class="{ 'filter-pill-active': rosterFilter === 'active' }" @click="changeRosterFilter('active')">
            Active
          </button>
          <button class="filter-pill" :class="{ 'filter-pill-active': rosterFilter === 'inactive' }" @click="changeRosterFilter('inactive')">
            Inactive
          </button>
        </div>
      </div>

      <p v-if="rosterError" class="staff-shell-error">
        {{ rosterError }}
      </p>

      <div class="staff-shell-body">
        <div v-if="rosterLoading" class="space-y-3">
          <div v-for="index in 8" :key="index" class="rounded-2xl border border-slate-200 bg-white p-4 animate-pulse dark:border-slate-700 dark:bg-slate-800/50">
            <div class="h-4 w-40 rounded bg-slate-200 dark:bg-slate-700"></div>
            <div class="mt-3 h-3 w-24 rounded bg-slate-200 dark:bg-slate-700"></div>
          </div>
        </div>

        <div v-else-if="filteredRoster.length" class="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
          <table class="w-full text-sm">
            <thead class="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
              <tr>
                <th class="px-4 py-3 text-left">Staff</th>
                <th class="px-4 py-3 text-left hidden lg:table-cell">Contact</th>
                <th class="px-4 py-3 text-left hidden xl:table-cell">Joined</th>
                <th class="px-4 py-3 text-left">Training status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="staff in filteredRoster"
                :key="staff.id"
                class="roster-row cursor-pointer"
                @click="goToStaff(staff)"
              >
                <td class="px-4 py-3">
                  <div class="flex items-center gap-3">
                    <div class="avatar-shell avatar-shell-sm">
                      <img v-if="photos[staff.id]" :src="photos[staff.id]" class="h-full w-full object-cover" />
                      <span v-else>{{ getInitials(staff.contact?.name) }}</span>
                    </div>
                    <div class="min-w-0">
                      <div class="flex flex-wrap items-center gap-2">
                        <p class="truncate font-semibold text-slate-900 dark:text-white">
                          {{ staff.contact?.name || `Staff #${staff.id}` }}
                        </p>
                        <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          :class="staff.active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'">
                          {{ staff.active ? "Active" : "Inactive" }}
                        </span>
                      </div>
                      <p class="truncate text-xs text-slate-500 dark:text-slate-400">
                        {{ staff.contact?.title || "No title set" }}
                      </p>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3 hidden lg:table-cell">
                  <p class="text-slate-700 dark:text-slate-200">
                    {{ staff.contact?.email || "No email" }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ staff.contact?.phone || "No phone" }}
                  </p>
                </td>
                <td class="px-4 py-3 hidden xl:table-cell text-slate-600 dark:text-slate-300">
                  {{ formatDate(staff.joined_at) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex flex-wrap gap-2">
                    <template v-if="alertLookup[staff.id]">
                      <span
                        v-if="alertLookup[staff.id].expired_count"
                        class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold badge-danger"
                      >
                        {{ alertLookup[staff.id].expired_count }} expired
                      </span>
                      <span
                        v-if="alertLookup[staff.id].expiring_soon_count"
                        class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold badge-soon"
                      >
                        {{ alertLookup[staff.id].expiring_soon_count }} due soon
                      </span>
                      <span
                        v-if="alertLookup[staff.id].missing_count"
                        class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold badge-warning"
                      >
                        {{ alertLookup[staff.id].missing_count }} missing
                      </span>
                    </template>
                    <span v-else class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold badge-clear">
                      Clear
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          No staff matched this view.
        </div>
      </div>

      <footer class="staff-shell-footer">
        <p class="text-xs text-slate-500 dark:text-slate-400">
          Page {{ rosterPage }} of {{ rosterLastPage }}
        </p>
        <PrevNextButtons
          :show-prev="rosterPage > 1"
          :show-next="rosterPage < rosterLastPage"
          @prev="changePage(-1)"
          @next="changePage(1)"
        />
      </footer>
    </section>

    <details class="box insights-shell p-0 overflow-hidden">
      <summary class="insights-summary">
        <div class="space-y-2">
          <p class="section-kicker">
            Insights
          </p>
          <div class="space-y-1">
            <h2 class="panel-title">
              Training, readiness, and activity
            </h2>
            <p class="insights-copy">
              Useful context when planning, kept separate from the day-to-day staff lookup flow.
            </p>
          </div>
        </div>

        <div class="insights-summary-side">
          <div class="insights-summary-toggle">
            <span class="toggle-closed">Show insights</span>
            <span class="toggle-open">Hide insights</span>
            <ChevronDownIcon class="h-4 w-4 insights-summary-chevron" />
          </div>
        </div>
      </summary>

      <div class="insights-body">
        <div class="insights-controls">
          <label class="month-picker">
            <CalendarDaysIcon class="h-4 w-4 text-slate-500 dark:text-slate-300" />
            <input v-model="selectedMonth" type="month" />
          </label>
          <button class="btn flex items-center gap-2" :disabled="dashboardLoading || rosterLoading" @click="refreshAll">
            <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': dashboardLoading || rosterLoading }" />
            Refresh insights
          </button>
        </div>

        <p v-if="dashboardError" class="staff-shell-error">
          {{ dashboardError }}
        </p>

        <section class="insights-group">
          <div class="insights-group-header">
            <p class="section-kicker">Overview</p>
            <p class="insights-group-copy">
              A small snapshot of the current staff picture.
            </p>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <article class="metric-card metric-card-embers">
              <div class="metric-header">
                <span>Total staff</span>
                <UserGroupIcon class="h-4 w-4 text-slate-400" />
              </div>
              <p class="metric-value">
                {{ summary.total_staff ?? 0 }}
              </p>
              <p class="metric-meta">
                {{ summary.active_staff ?? 0 }} active • {{ summary.inactive_staff ?? 0 }} inactive
              </p>
            </article>

            <article class="metric-card metric-card-emerald">
              <div class="metric-header">
                <span>New joiners (90d)</span>
                <IdentificationIcon class="h-4 w-4 text-slate-400" />
              </div>
              <p class="metric-value">
                {{ summary.new_joiners_90d ?? 0 }}
              </p>
              <p class="metric-meta">
                Recent additions visible in the active roster.
              </p>
            </article>

            <article class="metric-card metric-card-sky">
              <div class="metric-header">
                <span>Training expiring soon</span>
                <ClockIcon class="h-4 w-4 text-slate-400" />
              </div>
              <p class="metric-value">
                {{ summary.training_expiring_soon ?? 0 }}
              </p>
              <p class="metric-meta">
                {{ summary.staff_at_risk ?? 0 }} staff currently at risk.
              </p>
            </article>

            <article class="metric-card metric-card-rose">
              <div class="metric-header">
                <span>Expired or missing mandatory</span>
                <ExclamationTriangleIcon class="h-4 w-4 text-slate-400" />
              </div>
              <p class="metric-value">
                {{ (summary.training_expired ?? 0) + (summary.mandatory_gaps ?? 0) }}
              </p>
              <p class="metric-meta">
                {{ summary.training_expired ?? 0 }} expired • {{ summary.mandatory_gaps ?? 0 }} missing mandatory
              </p>
            </article>
          </div>
        </section>

        <section class="insights-group">
          <div class="insights-group-header">
            <p class="section-kicker">Activity</p>
            <p class="insights-group-copy">
              Recent staffing activity and how the roster is distributed.
            </p>
          </div>

          <div class="insights-stack">
            <article class="panel-card">
              <header class="panel-header">
                <div>
                  <p class="panel-kicker">Top workers</p>
                  <h3 class="panel-title">
                    Most active team members in {{ monthLabel }}
                  </h3>
                </div>
                <TrophyIcon class="h-5 w-5 text-slate-400" />
              </header>

              <div class="panel-body">
                <div v-if="dashboardLoading" class="grid gap-3 md:grid-cols-2">
                  <div v-for="index in 6" :key="index" class="rounded-2xl border border-slate-200 bg-white/80 p-4 animate-pulse dark:border-slate-700 dark:bg-slate-800/50">
                    <div class="h-4 w-32 rounded bg-slate-200 dark:bg-slate-700"></div>
                    <div class="mt-3 h-8 w-16 rounded bg-slate-200 dark:bg-slate-700"></div>
                    <div class="mt-3 h-3 w-24 rounded bg-slate-200 dark:bg-slate-700"></div>
                  </div>
                </div>

                <div v-else-if="dashboard.top_workers?.length" class="grid gap-3 md:grid-cols-2">
                  <button
                    v-for="worker in dashboard.top_workers"
                    :key="worker.staff.id"
                    type="button"
                    class="worker-card"
                    @click="goToStaff(worker.staff)"
                  >
                    <div class="flex items-center gap-3">
                      <div class="avatar-shell">
                        <img v-if="photos[worker.staff.id]" :src="photos[worker.staff.id]" class="h-full w-full object-cover" />
                        <span v-else>{{ getInitials(worker.staff.name) }}</span>
                      </div>
                      <div class="min-w-0 text-left">
                        <p class="truncate font-semibold text-slate-900 dark:text-white">
                          {{ worker.staff.name || `Staff #${worker.staff.id}` }}
                        </p>
                        <p class="truncate text-xs text-slate-500 dark:text-slate-400">
                          {{ worker.staff.primary_skill || "No primary skill" }}
                        </p>
                      </div>
                    </div>

                    <div class="worker-stats-grid">
                      <div class="worker-stat">
                        <span>Work days</span>
                        <strong>{{ worker.work_days }}</strong>
                      </div>
                      <div class="worker-stat">
                        <span>Jobs</span>
                        <strong>{{ worker.jobs_supported }}</strong>
                      </div>
                      <div class="worker-stat">
                        <span>Driver days</span>
                        <strong>{{ worker.driver_days }}</strong>
                      </div>
                    </div>
                  </button>
                </div>

                <div v-else class="empty-state">
                  No staffing activity found for {{ monthLabel }} yet.
                </div>
              </div>
            </article>

            <article class="panel-card">
              <header class="panel-header">
                <div>
                  <p class="panel-kicker">Coverage</p>
                  <h3 class="panel-title">
                    Skill mix and readiness
                  </h3>
                </div>
                <ChartBarSquareIcon class="h-5 w-5 text-slate-400" />
              </header>

              <div class="panel-body insights-substack">
                <section class="insight-block">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Primary skills
                    </h4>
                    <span class="text-xs text-slate-500 dark:text-slate-400">Active roster only</span>
                  </div>
                  <div v-if="skillMix.length" class="mt-3 space-y-3">
                    <div v-for="skill in skillMix" :key="skill.name" class="space-y-1">
                      <div class="flex items-center justify-between text-sm">
                        <span class="font-medium text-slate-700 dark:text-slate-200">{{ skill.name }}</span>
                        <span class="text-slate-500 dark:text-slate-400">{{ skill.count }} • {{ skill.percentage }}%</span>
                      </div>
                      <div class="skill-bar-track">
                        <div class="skill-bar-fill" :style="{ width: `${skill.percentage}%` }"></div>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-inline">
                    No primary skill data is available.
                  </div>
                </section>

                <section class="insight-block">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Readiness checks
                    </h4>
                    <CheckBadgeIcon class="h-4 w-4 text-slate-400" />
                  </div>
                  <div class="mt-3 grid gap-3 md:grid-cols-2">
                    <div v-for="item in readiness" :key="item.label" class="readiness-card">
                      <div class="flex items-start justify-between gap-3">
                        <div>
                          <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
                            {{ item.label }}
                          </p>
                          <p class="text-xs text-slate-500 dark:text-slate-400">
                            {{ item.count }} of {{ item.total }}
                          </p>
                        </div>
                        <span class="text-sm font-semibold text-slate-800 dark:text-slate-100">
                          {{ item.percentage }}%
                        </span>
                      </div>
                      <div class="skill-bar-track mt-3">
                        <div class="skill-bar-fill readiness-fill" :style="{ width: `${item.percentage}%` }"></div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </article>
          </div>
        </section>

        <section class="insights-group">
          <div class="insights-group-header">
            <p class="section-kicker">Follow-up</p>
            <p class="insights-group-copy">
              Risk items and suggested actions, split into smaller blocks.
            </p>
          </div>

          <div class="insights-stack">
            <article class="panel-card">
              <header class="panel-header">
                <div>
                  <p class="panel-kicker">Training watchlist</p>
                  <h3 class="panel-title">
                    Staff with expired or soon-to-expire training
                  </h3>
                </div>
                <ExclamationTriangleIcon class="h-5 w-5 text-slate-400" />
              </header>

              <div class="panel-body insights-substack">
                <div class="grid gap-3 sm:grid-cols-2">
                  <div class="mini-metric">
                    <span>Expired</span>
                    <strong>{{ dashboard.training_alerts?.summary?.expired ?? 0 }}</strong>
                  </div>
                  <div class="mini-metric">
                    <span>Due soon</span>
                    <strong>{{ dashboard.training_alerts?.summary?.expiring_soon ?? 0 }}</strong>
                  </div>
                  <div class="mini-metric">
                    <span>Missing mandatory</span>
                    <strong>{{ dashboard.training_alerts?.summary?.mandatory_missing ?? 0 }}</strong>
                  </div>
                </div>

                <div v-if="trainingWatchlist.length" class="space-y-3">
                  <button
                    v-for="entry in trainingWatchlist.slice(0, 8)"
                    :key="entry.staff.id"
                    type="button"
                    class="watchlist-row"
                    @click="goToStaff(entry.staff)"
                  >
                    <div class="min-w-0 text-left">
                      <div class="flex flex-wrap items-center gap-2">
                        <p class="font-semibold text-slate-900 dark:text-white">
                          {{ entry.staff.name || `Staff #${entry.staff.id}` }}
                        </p>
                        <span class="inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold"
                          :class="trainingTone({ expired_count: entry.expired_count, missing_count: entry.missing_count, expiring_soon_count: entry.expiring_soon_count })">
                          {{ entry.expired_count }} expired • {{ entry.expiring_soon_count }} soon • {{ entry.missing_count }} missing
                        </span>
                      </div>
                      <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {{ entry.staff.primary_skill || "No primary skill" }}
                      </p>
                      <div class="mt-3 flex flex-wrap gap-2">
                        <span
                          v-for="alert in entry.alerts.slice(0, 3)"
                          :key="`${entry.staff.id}-${alert.course_id}-${alert.status}`"
                          class="alert-chip"
                          :class="{
                            'alert-chip-danger': alert.status === 'expired',
                            'alert-chip-warning': alert.status === 'missing',
                            'alert-chip-soon': alert.status === 'expiring_soon',
                          }"
                        >
                          {{ alert.course }}
                          <template v-if="alert.status === 'expired'">
                            · expired {{ formatDate(alert.expires_on) }}
                          </template>
                          <template v-else-if="alert.status === 'expiring_soon'">
                            · due {{ formatDate(alert.expires_on) }}
                          </template>
                          <template v-else>
                            · missing
                          </template>
                        </span>
                      </div>
                    </div>
                  </button>
                </div>

                <div v-else class="empty-state">
                  No current training alerts.
                </div>

                <section v-if="courseBreakdown.length" class="course-breakdown">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Courses creating the most risk
                    </h4>
                    <ArrowTrendingUpIcon class="h-4 w-4 text-slate-400" />
                  </div>
                  <div class="mt-3 space-y-2">
                    <div v-for="course in courseBreakdown" :key="course.course" class="course-breakdown-row">
                      <span class="font-medium text-slate-800 dark:text-slate-100">{{ course.course }}</span>
                      <span class="text-xs text-slate-500 dark:text-slate-400">
                        {{ course.expired }} expired • {{ course.expiring_soon }} soon • {{ course.missing }} missing
                      </span>
                    </div>
                  </div>
                </section>
              </div>
            </article>

            <article class="panel-card">
              <header class="panel-header">
                <div>
                  <p class="panel-kicker">Suggestions</p>
                  <h3 class="panel-title">
                    Actions pulled from the current data
                  </h3>
                </div>
                <SparklesIcon class="h-5 w-5 text-slate-400" />
              </header>

              <div class="panel-body insights-substack">
                <section class="insight-block">
                  <div v-if="recommendations.length" class="space-y-3">
                    <div
                      v-for="item in recommendations"
                      :key="item.title"
                      class="recommendation-card"
                      :class="recommendationTone(item.tone)"
                    >
                      <p class="text-sm font-semibold text-slate-900 dark:text-white">
                        {{ item.title }}
                      </p>
                      <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                        {{ item.body }}
                      </p>
                    </div>
                  </div>
                </section>

                <section class="insight-block">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Recent joiners
                    </h4>
                    <CalendarDaysIcon class="h-4 w-4 text-slate-400" />
                  </div>
                  <div v-if="recentJoiners.length" class="mt-3 space-y-2">
                    <button
                      v-for="entry in recentJoiners"
                      :key="entry.staff.id"
                      type="button"
                      class="simple-row"
                      @click="goToStaff(entry.staff)"
                    >
                      <div>
                        <p class="font-medium text-slate-900 dark:text-white">
                          {{ entry.staff.name || `Staff #${entry.staff.id}` }}
                        </p>
                        <p class="text-xs text-slate-500 dark:text-slate-400">
                          Joined {{ formatDate(entry.joined_at) }} • {{ entry.staff.primary_skill || "No primary skill" }}
                        </p>
                      </div>
                      <span class="text-xs text-slate-400 dark:text-slate-500">
                        {{ formatRelative(entry.joined_at) }}
                      </span>
                    </button>
                  </div>
                  <div v-else class="empty-inline">
                    No recent joiners recorded.
                  </div>
                </section>

                <section class="insight-block">
                  <div class="flex items-center justify-between">
                    <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-200">
                      Missing core records
                    </h4>
                    <IdentificationIcon class="h-4 w-4 text-slate-400" />
                  </div>
                  <div v-if="missingCoreRecords.length" class="mt-3 space-y-2">
                    <button
                      v-for="entry in missingCoreRecords.slice(0, 5)"
                      :key="entry.staff.id"
                      type="button"
                      class="simple-row"
                      @click="goToStaff(entry.staff)"
                    >
                      <div class="min-w-0">
                        <p class="font-medium text-slate-900 dark:text-white">
                          {{ entry.staff.name || `Staff #${entry.staff.id}` }}
                        </p>
                        <p class="truncate text-xs text-slate-500 dark:text-slate-400">
                          Missing: {{ entry.missing.join(", ") }}
                        </p>
                      </div>
                      <span class="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-700/40 dark:text-slate-200">
                        {{ entry.missing_count }}
                      </span>
                    </button>
                  </div>
                  <div v-else class="empty-inline">
                    Core records look complete across the active roster.
                  </div>
                </section>
              </div>
            </article>
          </div>
        </section>
      </div>
    </details>
  </div>
</template>

<style scoped>
.staff-dashboard .box {
  margin-bottom: 0;
}

.section-kicker,
.panel-kicker {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #64748b;
}

.staff-shell,
.insights-shell,
.panel-card {
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: rgba(255, 255, 255, 0.98);
}

.staff-shell-header,
.staff-shell-toolbar,
.staff-shell-meta,
.staff-shell-footer,
.insights-body {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.staff-shell-header,
.staff-shell-toolbar,
.staff-shell-meta,
.staff-shell-footer {
  border-bottom: 1px solid rgba(226, 232, 240, 0.88);
}

.staff-shell-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1.5rem;
  padding-bottom: 1.25rem;
}

.staff-shell-title {
  font-size: 1.9rem;
  line-height: 1.1;
  font-weight: 650;
  color: #0f172a;
}

.staff-shell-copy {
  max-width: 42rem;
  font-size: 0.95rem;
  color: #475569;
}

.staff-shell-actions,
.staff-shell-filters,
.staff-shell-searches,
.insights-controls,
.insights-summary-side {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.staff-shell-actions {
  align-items: center;
  justify-content: flex-end;
}

.staff-shell-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.staff-shell-searches {
  flex: 1 1 26rem;
}

.staff-shell-filters {
  align-items: center;
}

.staff-shell-error {
  padding: 1rem 1.5rem 0;
  font-size: 0.9rem;
  color: #dc2626;
}

.staff-shell-body {
  padding: 1rem 1.5rem 1.25rem;
}

.staff-shell-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 0.9rem;
  padding-bottom: 0.9rem;
  border-bottom: 0;
}

.insights-summary {
  display: flex;
  cursor: pointer;
  list-style: none;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.35rem 1.5rem;
}

.insights-summary::-webkit-details-marker {
  display: none;
}

.insights-copy {
  max-width: 38rem;
  font-size: 0.9rem;
  color: #64748b;
}

.insights-summary-side {
  align-items: center;
  justify-content: flex-end;
  margin-left: auto;
}

.insights-summary-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
}

.toggle-open {
  display: none;
}

.insights-summary-chevron {
  transition: transform 150ms ease;
}

.insights-shell[open] .insights-summary {
  border-bottom: 1px solid rgba(226, 232, 240, 0.88);
}

.insights-shell[open] .insights-summary-chevron {
  transform: rotate(180deg);
}

.insights-shell[open] .toggle-closed {
  display: none;
}

.insights-shell[open] .toggle-open {
  display: inline;
}

.insights-body {
  padding-top: 1.25rem;
  padding-bottom: 1.5rem;
}

.insights-group + .insights-group {
  margin-top: 1.5rem;
}

.insights-group-header {
  margin-bottom: 0.9rem;
}

.insights-group-copy {
  margin-top: 0.25rem;
  font-size: 0.9rem;
  color: #64748b;
}

.insights-stack,
.insights-substack {
  display: grid;
  gap: 1rem;
}

.insight-block {
  border-radius: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.88);
  background: rgba(248, 250, 252, 0.72);
  padding: 1rem;
}

.insights-controls {
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.month-picker {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 9999px;
  border: 1px solid rgba(203, 213, 225, 0.95);
  background: rgba(248, 250, 252, 0.95);
  padding: 0.55rem 0.85rem;
  font-size: 0.875rem;
  color: #334155;
}

.month-picker input {
  border: 0;
  background: transparent;
  color: inherit;
}

.metric-card {
  border-radius: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.88);
  background: rgba(248, 250, 252, 0.85);
  padding: 1rem;
}

.metric-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.metric-value {
  margin-top: 0.85rem;
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1;
  color: #0f172a;
}

.metric-meta {
  margin-top: 0.5rem;
  font-size: 0.82rem;
  color: #64748b;
}

.panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  padding: 1.1rem 1.25rem 0.95rem;
}

.panel-title {
  font-size: 1.05rem;
  font-weight: 650;
  color: #0f172a;
}

.panel-body {
  padding: 1.1rem 1.25rem 1.25rem;
}

.worker-card {
  border-radius: 1.25rem;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(248, 250, 252, 0.9);
  padding: 1rem;
  text-align: left;
  transition: border-color 150ms ease, background-color 150ms ease;
}

.worker-card:hover {
  border-color: rgba(148, 163, 184, 0.8);
  background: rgba(241, 245, 249, 0.95);
}

.worker-stat {
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.65rem 0.75rem;
}

.worker-stat span {
  display: block;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.worker-stat strong {
  display: block;
  margin-top: 0.25rem;
  font-size: 1.15rem;
  color: #0f172a;
}

.worker-stats-grid {
  margin-top: 0.9rem;
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.avatar-shell {
  display: inline-flex;
  height: 3rem;
  width: 3rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 9999px;
  background: #e2e8f0;
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
}

.avatar-shell-sm {
  height: 2.5rem;
  width: 2.5rem;
}

.skill-bar-track {
  height: 0.55rem;
  overflow: hidden;
  border-radius: 9999px;
  background: rgba(226, 232, 240, 1);
}

.skill-bar-fill {
  height: 100%;
  border-radius: 9999px;
  background: #0f172a;
}

.readiness-fill {
  background: #334155;
}

.readiness-card,
.mini-metric,
.recommendation-card,
.simple-row,
.watchlist-row {
  border-radius: 1.1rem;
  border: 1px solid rgba(226, 232, 240, 0.88);
  background: rgba(255, 255, 255, 0.96);
}

.readiness-card,
.mini-metric,
.recommendation-card {
  padding: 0.95rem 1rem;
}

.mini-metric span {
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.mini-metric strong {
  display: block;
  margin-top: 0.45rem;
  font-size: 1.8rem;
  line-height: 1;
  color: #0f172a;
}

.watchlist-row,
.simple-row {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.95rem 1rem;
  text-align: left;
  transition: background-color 150ms ease, border-color 150ms ease;
}

.watchlist-row:hover,
.simple-row:hover {
  border-color: rgba(148, 163, 184, 0.78);
  background: rgba(248, 250, 252, 1);
}

.alert-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 0.3rem 0.65rem;
  font-size: 0.72rem;
  font-weight: 600;
}

.alert-chip-danger,
.badge-danger {
  background: rgba(254, 242, 242, 1);
  color: #b91c1c;
}

.alert-chip-warning,
.badge-warning {
  background: rgba(241, 245, 249, 1);
  color: #475569;
}

.alert-chip-soon,
.badge-soon {
  background: rgba(239, 246, 255, 1);
  color: #0369a1;
}

.badge-clear {
  background: rgba(240, 253, 244, 1);
  color: #15803d;
}

.recommendation-danger {
  border-color: rgba(254, 205, 211, 0.95);
  background: rgba(255, 241, 242, 0.88);
}

.recommendation-warning {
  border-color: rgba(203, 213, 225, 0.95);
  background: rgba(248, 250, 252, 0.95);
}

.recommendation-accent {
  border-color: rgba(186, 230, 253, 0.95);
  background: rgba(239, 246, 255, 0.9);
}

.recommendation-neutral {
  border-color: rgba(203, 213, 225, 0.95);
  background: rgba(248, 250, 252, 0.92);
}

.empty-state,
.empty-inline {
  border-radius: 1rem;
  color: #64748b;
}

.empty-state {
  border: 1px dashed rgba(203, 213, 225, 0.95);
  padding: 1.1rem 1rem;
  text-align: center;
}

.empty-inline {
  padding: 0.25rem 0;
  font-size: 0.9rem;
}

.filter-pill {
  border-radius: 9999px;
  border: 1px solid rgba(203, 213, 225, 0.95);
  background: rgba(248, 250, 252, 0.95);
  padding: 0.55rem 0.9rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #475569;
}

.filter-pill-active {
  border-color: rgba(148, 163, 184, 1);
  background: rgba(255, 255, 255, 1);
  color: #0f172a;
}

.roster-row {
  border-top: 1px solid rgba(226, 232, 240, 0.82);
  transition: background-color 150ms ease;
}

.roster-row:hover {
  background: rgba(248, 250, 252, 1);
}

.course-breakdown {
  border-radius: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.88);
  background: rgba(248, 250, 252, 0.85);
  padding: 1rem;
}

.course-breakdown-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.95);
  padding: 0.75rem 0.85rem;
}

@media (prefers-color-scheme: dark) {
  .staff-dashboard .section-kicker,
  .staff-dashboard .panel-kicker {
    color: #94a3b8;
  }

  .staff-dashboard .staff-shell,
  .staff-dashboard .insights-shell,
  .staff-dashboard .panel-card {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(30, 41, 59, 0.92);
  }

  .staff-dashboard .staff-shell-header,
  .staff-dashboard .staff-shell-toolbar,
  .staff-dashboard .insights-shell[open] .insights-summary {
    border-color: rgba(71, 85, 105, 0.82);
  }

  .staff-dashboard .staff-shell-title {
    color: #f8fafc;
  }

  .staff-dashboard .staff-shell-copy,
  .staff-dashboard .insights-copy,
  .staff-dashboard .insights-summary-toggle {
    color: #e2e8f0;
  }

  .staff-dashboard .month-picker {
    border-color: rgba(71, 85, 105, 0.82);
    background: rgba(51, 65, 85, 0.9);
    color: #e2e8f0;
  }

  .staff-dashboard .month-picker input {
    color-scheme: dark;
  }

  .staff-dashboard .metric-card {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(51, 65, 85, 0.88);
  }

  .staff-dashboard .metric-header {
    color: #94a3b8;
  }

  .staff-dashboard .metric-value {
    color: #f8fafc;
  }

  .staff-dashboard .metric-meta {
    color: #cbd5e1;
  }

  .staff-dashboard .panel-card {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(30, 41, 59, 0.88);
  }

  .staff-dashboard .insights-group-copy {
    color: #cbd5e1;
  }

  .staff-dashboard .panel-header {
    border-bottom-color: rgba(51, 65, 85, 0.8);
  }

  .staff-dashboard .panel-title {
    color: #f8fafc;
  }

  .staff-dashboard .worker-card {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(51, 65, 85, 0.74);
  }

  .staff-dashboard .worker-card:hover {
    border-color: rgba(100, 116, 139, 0.86);
    background: rgba(71, 85, 105, 0.76);
  }

  .staff-dashboard .worker-stat {
    background: rgba(30, 41, 59, 0.82);
  }

  .staff-dashboard .worker-stat span {
    color: #94a3b8;
  }

  .staff-dashboard .worker-stat strong {
    color: #f8fafc;
  }

  .staff-dashboard .avatar-shell {
    background: rgba(51, 65, 85, 0.95);
    color: #e2e8f0;
  }

  .staff-dashboard .skill-bar-track {
    background: rgba(51, 65, 85, 0.92);
  }

  .staff-dashboard .skill-bar-fill {
    background: #e2e8f0;
  }

  .staff-dashboard .readiness-fill {
    background: #94a3b8;
  }

  .staff-dashboard .readiness-card,
  .staff-dashboard .mini-metric,
  .staff-dashboard .recommendation-card,
  .staff-dashboard .simple-row,
  .staff-dashboard .watchlist-row {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(51, 65, 85, 0.72);
  }

  .staff-dashboard .insight-block {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(30, 41, 59, 0.5);
  }

  .staff-dashboard .mini-metric span {
    color: #94a3b8;
  }

  .staff-dashboard .mini-metric strong {
    color: #f8fafc;
  }

  .staff-dashboard .watchlist-row:hover,
  .staff-dashboard .simple-row:hover {
    border-color: rgba(100, 116, 139, 0.86);
    background: rgba(71, 85, 105, 0.7);
  }

  .staff-dashboard .alert-chip-danger,
  .staff-dashboard .badge-danger {
    background: rgba(244, 63, 94, 0.16);
    color: #fda4af;
  }

  .staff-dashboard .alert-chip-warning,
  .staff-dashboard .badge-warning {
    background: rgba(100, 116, 139, 0.24);
    color: #e2e8f0;
  }

  .staff-dashboard .alert-chip-soon,
  .staff-dashboard .badge-soon {
    background: rgba(14, 165, 233, 0.16);
    color: #7dd3fc;
  }

  .staff-dashboard .badge-clear {
    background: rgba(16, 185, 129, 0.16);
    color: #6ee7b7;
  }

  .staff-dashboard .recommendation-danger {
    border-color: rgba(136, 19, 55, 0.5);
    background: rgba(127, 29, 29, 0.34);
  }

  .staff-dashboard .recommendation-warning {
    border-color: rgba(71, 85, 105, 0.82);
    background: rgba(51, 65, 85, 0.74);
  }

  .staff-dashboard .recommendation-accent {
    border-color: rgba(12, 74, 110, 0.6);
    background: rgba(14, 116, 144, 0.28);
  }

  .staff-dashboard .recommendation-neutral {
    border-color: rgba(71, 85, 105, 0.82);
    background: rgba(51, 65, 85, 0.72);
  }

  .staff-dashboard .empty-state,
  .staff-dashboard .empty-inline {
    color: #94a3b8;
  }

  .staff-dashboard .empty-state {
    border-color: rgba(71, 85, 105, 0.82);
    background: rgba(51, 65, 85, 0.42);
  }

  .staff-dashboard .filter-pill {
    border-color: rgba(71, 85, 105, 0.82);
    background: rgba(51, 65, 85, 0.86);
    color: #cbd5e1;
  }

  .staff-dashboard .filter-pill-active {
    border-color: rgba(148, 163, 184, 0.88);
    background: rgba(71, 85, 105, 0.88);
    color: #f8fafc;
  }

  .staff-dashboard .roster-row {
    border-top-color: rgba(51, 65, 85, 0.82);
  }

  .staff-dashboard .roster-row:hover {
    background: rgba(51, 65, 85, 0.58);
  }

  .staff-dashboard .course-breakdown {
    border-color: rgba(51, 65, 85, 0.86);
    background: rgba(51, 65, 85, 0.7);
  }

  .staff-dashboard .course-breakdown-row {
    background: rgba(30, 41, 59, 0.82);
  }
}

@media (max-width: 767px) {
  .staff-shell-header,
  .staff-shell-toolbar,
  .staff-shell-footer,
  .staff-shell-body,
  .insights-summary,
  .insights-body,
  .panel-header,
  .panel-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .staff-shell-title {
    font-size: 1.55rem;
  }

  .staff-shell-footer,
  .insights-controls,
  .insights-summary-side {
    align-items: flex-start;
    justify-content: flex-start;
  }

}

@media (min-width: 640px) {
  .worker-stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>
